import { useCallback, useEffect, useRef, useState } from "react";

import { socket } from "../../socket/socket";
import { fetchLists, fetchTasks } from "./todoApi";
import { errorText, isOlderVersion, sortLists } from "./todoUtils";

/** Вернулись на вкладку — списки перечитываем не чаще чем раз в столько. */
const FOCUS_RELOAD_MS = 30000;

/** Переключатели задачи, которые показываем сразу, не дожидаясь ответа сервера. */
const QUICK_FIELDS = ["status", "priority", "pinned"];

/** Из правки задачи — только переключатели (или null, если их в правке нет). */
export const pickQuick = (patch = {}) => {
    const picked = {};
    QUICK_FIELDS.forEach((key) => {
        if (patch[key] !== undefined) picked[key] = patch[key];
    });
    return Object.keys(picked).length ? picked : null;
};

/*
 * Данные раздела «Задачи»: списки и задачи открытых списков.
 *
 * Изменения приходят с сервера событием «todo:event» (тот же сокет, что у
 * чатов): новая задача, смена статуса, расшифровка голосового появляются на
 * экране сами — и те, что сделаны с телефона, тоже.
 */
export default function useTodoData(meId) {
    const [lists, setLists] = useState([]);
    const [listsLoaded, setListsLoaded] = useState(false);
    const [listsError, setListsError] = useState("");
    /** Задачи по спискам (вместе с подзадачами). */
    const [tasksByList, setTasksByList] = useState({});
    const [tasksLoading, setTasksLoading] = useState({});
    const [tasksError, setTasksError] = useState({});
    /** Растёт с каждым событием сервера: по нему календарь понимает, что пора перечитать. */
    const [revision, setRevision] = useState(0);

    const aliveRef = useRef(true);
    const loadedRef = useRef(new Set());
    const loadingRef = useRef(new Set());
    const lastListsLoadRef = useRef(0);
    /**
     * Что человек уже нажал, а сервер ещё не подтвердил:
     * id задачи → { count — сколько запросов в пути, values — что показываем, base — что на сервере }.
     */
    const quickRef = useRef(new Map());

    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    // ---------- изменения состояния ----------

    const applyList = useCallback((list) => {
        if (!list?._id) return;
        setLists((current) => sortLists(
            current.some((item) => item._id === list._id)
                ? current.map((item) => (item._id === list._id ? list : item))
                : [list, ...current],
            meId
        ));
    }, [meId]);

    const removeList = useCallback((listId) => {
        const id = String(listId || "");
        if (!id) return;

        loadedRef.current.delete(id);
        setLists((current) => current.filter((item) => item._id !== id));
        setTasksByList((current) => {
            if (!current[id]) return current;
            const next = { ...current };
            delete next[id];
            return next;
        });
    }, []);

    /**
     * Версия задачи с сервера + то, что человек уже нажал. Без этого при двух
     * быстрых нажатиях кружок статуса дёргался бы назад на первый ответ.
     */
    const overlayQuick = useCallback((task) => {
        const entry = task?._id ? quickRef.current.get(task._id) : null;
        if (!entry) return task;

        // Заодно запоминаем, что на самом деле лежит на сервере.
        Object.keys(entry.values).forEach((key) => {
            entry.base[key] = task[key];
        });
        return { ...task, ...entry.values };
    }, []);

    /** Поправить задачу на экране, не трогая сервер. */
    const patchLocal = useCallback((taskId, values) => {
        setTasksByList((current) => {
            const listId = Object.keys(current).find(
                (key) => current[key].some((item) => item._id === taskId)
            );
            if (!listId) return current;

            return {
                ...current,
                [listId]: current[listId].map((item) => (item._id === taskId ? { ...item, ...values } : item)),
            };
        });
    }, []);

    /** Положить на экран задачу с сервера. Возвращает то, что в итоге показано. */
    const applyTask = useCallback((incoming) => {
        if (!incoming?._id || !incoming.listId) return incoming;
        const task = overlayQuick(incoming);

        setTasksByList((current) => {
            const items = current[task.listId];
            // Список ещё не открывали — его задачи загрузятся целиком при открытии.
            if (!items) return current;

            const shown = items.find((item) => item._id === task._id);
            // Ответы сервера могут прийти не по порядку: старую версию поверх новой не кладём.
            if (shown && isOlderVersion(task, shown)) return current;

            return {
                ...current,
                [task.listId]: shown
                    ? items.map((item) => (item._id === task._id ? task : item))
                    : [task, ...items],
            };
        });

        return task;
    }, [overlayQuick]);

    /**
     * Нажали переключатель (статус, «важная», «закрепить»): показываем сразу.
     * task — задача, как она сейчас на экране.
     */
    const startQuick = useCallback((task, values) => {
        const id = String(task?._id || "");
        if (!id || !values) return;

        const entry = quickRef.current.get(id) || { count: 0, values: {}, base: {} };
        Object.keys(values).forEach((key) => {
            if (!(key in entry.base)) entry.base[key] = task[key];
            entry.values[key] = values[key];
        });
        entry.count += 1;
        quickRef.current.set(id, entry);

        patchLocal(id, values);
    }, [patchLocal]);

    /**
     * Запрос переключателя закончился. Если он не прошёл (failed) и других в
     * пути нет — возвращаем на экран то, что на сервере. Возвращает эти значения.
     */
    const finishQuick = useCallback((taskId, failed = false) => {
        const id = String(taskId || "");
        const entry = quickRef.current.get(id);
        if (!entry) return null;

        entry.count -= 1;
        if (entry.count > 0) return null;

        quickRef.current.delete(id);
        if (!failed) return null;

        patchLocal(id, entry.base);
        return entry.base;
    }, [patchLocal]);

    const removeTasks = useCallback((listId, taskIds = []) => {
        const removed = new Set(taskIds.map(String));
        if (!listId || !removed.size) return;

        setTasksByList((current) => {
            const items = current[listId];
            if (!items) return current;
            return { ...current, [listId]: items.filter((item) => !removed.has(item._id)) };
        });
    }, []);

    // ---------- загрузка ----------

    const loadLists = useCallback(async () => {
        lastListsLoadRef.current = Date.now();
        setListsError("");

        try {
            const next = await fetchLists();
            if (!aliveRef.current) return;
            setLists(sortLists(next, meId));
            setListsLoaded(true);
        } catch (error) {
            if (!aliveRef.current) return;
            setListsError(errorText(error, "Не удалось загрузить списки задач"));
        }
    }, [meId]);

    const loadTasks = useCallback(async (listId, { force = false } = {}) => {
        const id = String(listId || "");
        if (!id || loadingRef.current.has(id)) return;
        if (!force && loadedRef.current.has(id)) return;

        loadingRef.current.add(id);
        setTasksLoading((current) => ({ ...current, [id]: true }));
        setTasksError((current) => ({ ...current, [id]: "" }));

        try {
            const result = await fetchTasks(id);
            if (!aliveRef.current) return;

            loadedRef.current.add(id);
            setTasksByList((current) => ({ ...current, [id]: result.tasks.map(overlayQuick) }));
            if (result.list) applyList(result.list);
        } catch (error) {
            if (!aliveRef.current) return;

            // Списка больше нет (удалили или нас из него убрали).
            if (Number(error?.response?.status || 0) === 404) removeList(id);
            setTasksError((current) => ({ ...current, [id]: errorText(error, "Не удалось загрузить задачи") }));
        } finally {
            loadingRef.current.delete(id);
            if (aliveRef.current) setTasksLoading((current) => ({ ...current, [id]: false }));
        }
    }, [applyList, overlayQuick, removeList]);

    /** Перечитать всё, что уже открыто (связь вернулась, нажали «Обновить»). */
    const reloadAll = useCallback(() => {
        loadLists();
        [...loadedRef.current].forEach((listId) => loadTasks(listId, { force: true }));
        setRevision((value) => value + 1);
    }, [loadLists, loadTasks]);

    useEffect(() => {
        if (!meId) return;
        loadLists();
    }, [loadLists, meId]);

    // ---------- события с сервера ----------

    useEffect(() => {
        if (!meId) return undefined;

        const onEvent = (payload = {}) => {
            setRevision((value) => value + 1);

            if (payload.kind === "list") applyList(payload.list);
            else if (payload.kind === "list_removed") removeList(payload.listId);
            else if (payload.kind === "task") applyTask(payload.task);
            else if (payload.kind === "task_removed") {
                removeTasks(String(payload.listId || ""), Array.isArray(payload.taskIds) ? payload.taskIds : []);
            }
        };

        // Пока связи не было, события могли пройти мимо — перечитываем.
        const onConnect = () => reloadAll();

        const onFocus = () => {
            if (Date.now() - lastListsLoadRef.current > FOCUS_RELOAD_MS) reloadAll();
        };

        socket.on("todo:event", onEvent);
        socket.on("connect", onConnect);
        window.addEventListener("focus", onFocus);

        return () => {
            socket.off("todo:event", onEvent);
            socket.off("connect", onConnect);
            window.removeEventListener("focus", onFocus);
        };
    }, [applyList, applyTask, meId, reloadAll, removeList, removeTasks]);

    return {
        lists,
        listsLoaded,
        listsError,
        tasksByList,
        tasksLoading,
        tasksError,
        revision,
        loadLists,
        loadTasks,
        reloadAll,
        applyList,
        removeList,
        applyTask,
        removeTasks,
        overlayQuick,
        startQuick,
        finishQuick,
    };
}
